import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  MessageCircle,
  Sparkles,
  UserPlus,
} from "lucide-react";
import { api, formatDateBR } from "@/lib/api";

const onlyDigits = (s) => (s || "").toString().replace(/\D+/g, "");

/**
 * NewRegistrationsAlert
 * ---------------------
 * Admin dashboard widget listing schools whose director completed registration
 * (first-ever user) and the admin hasn't acknowledged yet. Actions per row:
 *   - WhatsApp (saudar/boas-vindas)
 *   - OK, ciente (dismiss)
 *   - Ver escola (link)
 */
export default function NewRegistrationsAlert() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/registrations/pending-ack", { silent: true });
      setItems(r.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const acknowledge = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/registrations/${s.id}/ack`);
      toast.success(`${s.name} removida do painel`);
      setItems((arr) => arr.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar");
    } finally {
      setBusyId(null);
    }
  };

  const acknowledgeAll = async () => {
    if (!window.confirm(`Marcar TODAS as ${items.length} escolas como ciente?`)) return;
    setBusyId("__all__");
    try {
      const r = await api.post(`/registrations/ack-all`);
      toast.success(`${r.data?.updated || 0} escolas marcadas como ciente`);
      setItems([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar todas");
    } finally {
      setBusyId(null);
    }
  };

  const whatsappLink = (s) => {
    const phone = onlyDigits(s.whatsapp_director || s.director_phone || s.phone || "");
    if (!phone) return null;
    const name = (s.director_name || "").trim() || s.registered_user_name || "Diretor(a)";
    const text = `🤖 *Mensagem automática — AMC Escolas*\n\nOlá ${name}! Vi aqui que você acabou de concluir o cadastro da ${s.name} no nosso novo sistema AMC Escolas. Seja muito bem-vindo(a)! Qualquer dúvida é só me chamar por aqui. 🤝`;
    const full = phone.startsWith("55") ? phone : `55${phone}`;
    return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
  };

  if (loading) return null;
  if (!items.length) return null;

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 rounded-2xl overflow-hidden mb-6"
      data-testid="new-registrations-alert"
    >
      <div className="px-4 py-3 flex items-center justify-between gap-2 border-b border-emerald-200/80 bg-white/60 backdrop-blur">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shrink-0 shadow-sm">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.4} />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm font-black text-emerald-900 leading-tight">
              Novas escolas cadastradas
            </p>
            <p className="text-[11px] text-emerald-800/80 leading-tight">
              {items.length === 1
                ? "1 cadastro recente aguardando"
                : `${items.length} cadastros recentes aguardando`}
            </p>
          </div>
        </div>
        {items.length > 1 && (
          <button
            type="button"
            onClick={acknowledgeAll}
            disabled={busyId === "__all__"}
            data-testid="new-registrations-ack-all"
            className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
          >
            {busyId === "__all__" ? "Confirmando..." : "OK, ciente de todas"}
          </button>
        )}
      </div>
      <ul className="divide-y divide-emerald-200/50">
        {items.map((s) => {
          const wa = whatsappLink(s);
          return (
            <li
              key={s.id}
              data-testid={`new-registration-${s.id}`}
              className="p-3 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0 border border-emerald-300">
                  <UserPlus className="w-4 h-4 text-emerald-700" strokeWidth={2.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black text-slate-900 leading-tight truncate">
                    {s.name}
                  </p>
                  <p className="text-[11px] text-slate-600 leading-tight truncate">
                    {s.registered_user_name && `${s.registered_user_name} • `}
                    {s.registered_user_email}
                  </p>
                  <p className="text-[10px] text-emerald-700 font-semibold mt-0.5">
                    Cadastrou em {formatDateBR(s.registered_at)}
                    {s.city && ` • ${s.city}${s.state ? "/" + s.state : ""}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {wa && (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`new-registration-wa-${s.id}`}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-[11px] font-bold"
                    title="Saudar no WhatsApp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </a>
                )}
                <Link
                  to={`/escolas/${s.id}`}
                  data-testid={`new-registration-view-${s.id}`}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-[11px] font-bold"
                >
                  <span className="hidden sm:inline">Ver escola</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={() => acknowledge(s)}
                  disabled={busyId === s.id}
                  data-testid={`new-registration-ack-${s.id}`}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold disabled:opacity-60"
                >
                  {busyId === s.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  )}
                  OK
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
